/**
 * @file Renders the modal that collects the new email address.
 */
import { Form } from "antd";

import InputExtra from "@components/Form/InputExtra";
import { getConfig } from "@components/Config";
import { UpdateModal } from "@components/Page";
import { MODAL_WIDTH2 } from "@components/Constants";
import { useAppContext } from "@components/AppContext";
import type { AppContext } from "@components/AppContext/types";
import { changeMyIdentityEmailOperation as operation } from "@api/services/identity";

import type { ModalRef } from "../../Page/Modal/types";

const PLATFORM_NAME = getConfig<string>("PLATFORM_NAME");

const LABEL_EMAIL = "New Email Address";
const LABEL_ENTITY = "Email";
const LABEL_CONTINUE = "Continue";
const LABEL_CHANGE_EMAIL = "Change Email";
const LABEL_FAILED_MESSAGE = "Failed to change the email";
const LABEL_EMAIL_EXTRA = `${PLATFORM_NAME} sends an email verification code to
the specified address. Be sure to check the spam folder in case you can't find
it in your inbox.`;

type FormValues = {
  email: string;
};

type Props = {
  modal: ModalRef;
  onSuccess: (email: string) => unknown;
};

/**
 * Renders the modal that submits the new email address and starts the
 * verification flow.
 */
const ChangeEmailModal = ({
  modal,
  onSuccess: onSuccessCustom,
}: Props): JSX.Element => {
  const [ form ] = Form.useForm<FormValues>();

  const { authenticatedRequest: request } = useAppContext() as AppContext;

  const schema = operation.getSchema([
    {
      name: "email",
      label: LABEL_EMAIL,
      itemProps: {
        extra: <InputExtra text={LABEL_EMAIL_EXTRA} />,
      },
    },
  ]);

  /**
   * Passes the submitted email address to the custom success handler.
   */
  const onSuccess = () => {
    const { email } = form.getFieldsValue();

    return onSuccessCustom(email);
  };

  const updateModalProps = {
    width: MODAL_WIDTH2,
    title: LABEL_CHANGE_EMAIL,
    entity: LABEL_ENTITY,
    submitLabel: LABEL_CONTINUE,
    failedMessage: LABEL_FAILED_MESSAGE,
    successMessage: false,
    form,
    modal,
    schema,
    request,
    onSuccess,
    operation,
  };

  return (
    <UpdateModal {...updateModalProps} />
  );
};

export default ChangeEmailModal;
